import { call } from '@advanza/api'
import { Button } from '@advanza/ui'
import {
    GetProfileChecksResponse,
    PremiumCancelProfileCheck,
    PremiumCancelProfileCheckAlignment,
} from '@trustoo/types/api/controllers/churnReasonThemes'
import clsx from 'clsx'
import { useToast } from 'components/misc/Toaster'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

const BASE_URL = 'office/churn-reason-themes'
const PAGE_SIZE = 50

const ALIGNMENT_BADGES: Record<
    PremiumCancelProfileCheckAlignment,
    { label: string; className: string }
> = {
    aligned: { label: 'Aligned', className: 'bg-[#dcfce7] text-[#166534]' },
    partly_aligned: { label: 'Partly aligned', className: 'bg-[#fef3c7] text-[#92400e]' },
    not_aligned: { label: 'Not aligned', className: 'bg-[#fee2e2] text-[#991b1b]' },
    insufficient_data: { label: 'Insufficient data', className: 'bg-[#f3f4f6] text-[#4b5563]' },
}

const formatDate = (value: string | null) =>
    value === null ? '' : new Date(value).toLocaleString('nl-NL')

interface AlignmentBadgeProps {
    check: PremiumCancelProfileCheck
}

const AlignmentBadge = ({ check }: AlignmentBadgeProps) => {
    if (check.status === 'failed') {
        return (
            <span className="rounded-full bg-[#fee2e2] px-2 py-0.5 text-sm font-medium text-[#991b1b]">
                Failed
            </span>
        )
    }
    if (check.alignment === null) {
        return (
            <span className="rounded-full bg-[#f3f4f6] px-2 py-0.5 text-sm font-medium text-[#4b5563]">
                Pending
            </span>
        )
    }
    const badge = ALIGNMENT_BADGES[check.alignment]
    return (
        <span className={clsx('rounded-full px-2 py-0.5 text-sm font-medium', badge.className)}>
            {badge.label}
        </span>
    )
}

const PremiumCancelProfileChecksList = () => {
    const { addToast } = useToast()
    const [items, setItems] = useState<PremiumCancelProfileCheck[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [expandedIds, setExpandedIds] = useState<number[]>([])

    const fetchPage = async (offset: number) => {
        setLoading(true)
        try {
            const response: GetProfileChecksResponse = await call(
                `${BASE_URL}/get-profile-checks?limit=${PAGE_SIZE}&offset=${offset}`,
            )
            setItems((current) => {
                const page = response.items ?? []
                if (offset === 0) {
                    return page
                }
                // Newest first and paged by offset: a cancellation that came in between two loads
                // shifts every row down one place, so a page can repeat the last row shown.
                const shown = new Set(current.map((item) => item.premium_cancel_profile_check_id))
                return [
                    ...current,
                    ...page.filter((item) => !shown.has(item.premium_cancel_profile_check_id)),
                ]
            })
            setTotal(response.total ?? 0)
        } catch {
            addToast({ msg: 'Failed to load profile checks.', red: true })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchPage(0)
    }, [])

    const toggleText = (checkId: number) => {
        setExpandedIds((current) =>
            current.includes(checkId)
                ? current.filter((id) => id !== checkId)
                : [...current, checkId],
        )
    }

    if (loading && items.length === 0) {
        return <div>Loading...</div>
    }

    if (items.length === 0) {
        return <p>No profile checks yet.</p>
    }

    return (
        <div>
            <table className="w-full border-collapse">
                <thead>
                    <tr className="border-b-2 border-[#dddddd]">
                        <th className="p-2 text-left align-top">Cancelled</th>
                        <th className="p-2 text-left align-top">Provider</th>
                        <th className="p-2 text-left align-top">Reason</th>
                        <th className="p-2 text-left align-top">Verdict</th>
                        <th className="p-2 text-left align-top">Explanation</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((check) => {
                        const isExpanded = expandedIds.includes(
                            check.premium_cancel_profile_check_id,
                        )

                        return (
                            <tr
                                key={check.premium_cancel_profile_check_id}
                                className="border-b border-[#eeeeee]">
                                <td className="p-2 text-left align-top whitespace-nowrap">
                                    <div>{formatDate(check.cancelled_at)}</div>
                                    <div className="text-sm">
                                        #{check.premium_cancel_request_id}
                                    </div>
                                </td>
                                <td className="p-2 text-left align-top">
                                    <Link
                                        to={`/service/providers/provider/${check.service_provider_id}`}>
                                        {check.business_name ??
                                            `Provider ${check.service_provider_id}`}
                                    </Link>
                                    <div className="text-sm">
                                        #{check.service_provider_id} ·{' '}
                                        {check.country_code.toUpperCase()}
                                    </div>
                                </td>
                                <td className="p-2 text-left align-top">
                                    <div className="font-bold">
                                        {check.theme_label ??
                                            check.reason_label ??
                                            `Reason ${check.reason_id}`}
                                    </div>
                                    <div className="flex flex-wrap gap-1 text-sm">
                                        {check.theme_label !== null && <span>theme</span>}
                                        {check.has_analysis ? (
                                            <span>AI flow</span>
                                        ) : (
                                            <span>full list</span>
                                        )}
                                        {check.corrected_by_provider === true && (
                                            <span>· corrected the suggestion</span>
                                        )}
                                    </div>
                                    <div className="text-sm">
                                        <Button
                                            name="link"
                                            onClick={() =>
                                                toggleText(check.premium_cancel_profile_check_id)
                                            }>
                                            {isExpanded ? 'Hide text' : 'Show text'}
                                        </Button>
                                        {isExpanded && (
                                            <p className="mt-1">
                                                <em>{check.provider_text}</em>
                                            </p>
                                        )}
                                    </div>
                                </td>
                                <td className="p-2 text-left align-top whitespace-nowrap">
                                    <AlignmentBadge check={check} />
                                    {check.status === 'done' && check.latency_ms !== null && (
                                        <div className="mt-1 text-sm">{check.latency_ms} ms</div>
                                    )}
                                    {check.attempts > 1 && (
                                        <div className="text-sm">{check.attempts} attempts</div>
                                    )}
                                    {check.snapshot_before_cancellation === false && (
                                        <div className="text-sm">snapshot taken after cancel</div>
                                    )}
                                </td>
                                <td className="p-2 text-left align-top">
                                    {check.status === 'failed' ? (
                                        <span className="text-[#991b1b]">
                                            {check.error ?? 'Failed without an error message'}
                                        </span>
                                    ) : (
                                        check.explanation
                                    )}
                                    {(check.supporting_signals.length > 0 ||
                                        check.contradicting_signals.length > 0) && (
                                        <div className="mt-1 text-sm">
                                            {check.supporting_signals.length > 0 && (
                                                <div>
                                                    For: {check.supporting_signals.join('; ')}
                                                </div>
                                            )}
                                            {check.contradicting_signals.length > 0 && (
                                                <div>
                                                    Against:{' '}
                                                    {check.contradicting_signals.join('; ')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {check.status === 'done' && check.error !== null && (
                                        <div className="mt-1 text-sm text-[#991b1b]">
                                            Last re-run failed: {check.error}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            <div className="mt-4 flex items-center gap-4">
                <span className="text-sm">
                    {items.length} of {total}
                </span>
                {items.length < total && (
                    <Button disabled={loading} onClick={() => fetchPage(items.length)}>
                        Load more
                    </Button>
                )}
            </div>
        </div>
    )
}

export default PremiumCancelProfileChecksList
